import { useQuery } from '@tanstack/react-query'
import { useNetworkEnvironment } from '@/hooks/useNetworkEnvironment'
import { monitoringService } from '@/services/admin/monitoringService'

export const testnetFaucetMonitoringQueryKeys = {
  summary: (mode: string) => ['admin', 'testnet-faucet', 'summary', mode] as const,
  claims: (mode: string, params: Record<string, unknown>) => [
    'admin',
    'testnet-faucet',
    'claims',
    mode,
    params,
  ] as const,
  items: (mode: string, claimId: string | null) => [
    'admin',
    'testnet-faucet',
    'items',
    mode,
    claimId,
  ] as const,
  cooldowns: (mode: string, params: Record<string, unknown>) => [
    'admin',
    'testnet-faucet',
    'cooldowns',
    mode,
    params,
  ] as const,
}

export function useTestnetFaucetSummary(options?: { enabled?: boolean }) {
  const { currentEnv } = useNetworkEnvironment()
  return useQuery({
    queryKey: testnetFaucetMonitoringQueryKeys.summary(currentEnv),
    queryFn: () => monitoringService.getTestnetFaucetSummary(currentEnv),
    enabled: currentEnv === 'testnet' && (options?.enabled ?? true),
    refetchInterval: options?.enabled === false ? false : 30_000,
    staleTime: 15_000,
  })
}

export function useTestnetFaucetClaims(
  params: {
    page?: number
    limit?: number
    status?: string
    packageType?: string
    userEmail?: string
    recipientAddress?: string
  },
  options?: { enabled?: boolean }
) {
  const { currentEnv } = useNetworkEnvironment()
  return useQuery({
    queryKey: testnetFaucetMonitoringQueryKeys.claims(currentEnv, params),
    queryFn: () => monitoringService.getTestnetFaucetClaims(params, currentEnv),
    enabled: currentEnv === 'testnet' && (options?.enabled ?? true),
    staleTime: 0,
  })
}

export function useTestnetFaucetItems(
  claimId: string | null,
  options?: { enabled?: boolean }
) {
  const { currentEnv } = useNetworkEnvironment()
  return useQuery({
    queryKey: testnetFaucetMonitoringQueryKeys.items(currentEnv, claimId),
    queryFn: () => monitoringService.getTestnetFaucetItems(claimId as string, currentEnv),
    enabled: currentEnv === 'testnet' && Boolean(claimId) && (options?.enabled ?? true),
    staleTime: 0,
  })
}

export function useTestnetFaucetCooldowns(
  params: {
    page?: number
    limit?: number
    context?: string
    status?: string
    identifier?: string
  },
  options?: { enabled?: boolean }
) {
  const { currentEnv } = useNetworkEnvironment()
  return useQuery({
    queryKey: testnetFaucetMonitoringQueryKeys.cooldowns(currentEnv, params),
    queryFn: () => monitoringService.getTestnetFaucetCooldowns(params, currentEnv),
    enabled: currentEnv === 'testnet' && (options?.enabled ?? true),
    staleTime: 0,
  })
}
